import React, { useEffect, useMemo, useState } from "react";
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowDownRight, ArrowUpRight, CalendarCheck, IndianRupee, Landmark, TicketPercent, TrendingUp } from "lucide-react";
import { loadLedger } from "../../services/financeService";

const DAY = 86400000;

const RANGES = {
  "7days": { label: "Last 7 days", buckets: 7, unit: "day" },
  "30days": { label: "Last 30 days", buckets: 30, unit: "day" },
  "3months": { label: "Last 3 months", buckets: 3, unit: "month" },
  "6months": { label: "Last 6 months", buckets: 6, unit: "month" },
  "1year": { label: "Last 12 months", buckets: 12, unit: "month" },
};

const METRICS = {
  revenue: { label: "Revenue", hint: "Paid by patients", icon: IndianRupee, money: true },
  bookings: { label: "Bookings", hint: "Paid consultations", icon: CalendarCheck, money: false },
  earnings: { label: "Platform earnings", hint: "Revenue minus doctor payouts", icon: Landmark, money: true },
  discounts: { label: "Discounts", hint: "Coupon value given to patients", icon: TicketPercent, money: true, lowerIsBetter: true },
};

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const inrShort = (n) =>
  n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : n >= 1000 ? `₹${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `₹${Math.round(n)}`;

const startOfDay = (ms) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const startOfMonth = (ms) => {
  const d = new Date(ms);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const addMonths = (ms, n) => {
  const d = new Date(ms);
  d.setMonth(d.getMonth() + n);
  return d.getTime();
};

/** Bucket start times for the period, oldest → newest, plus the same number of buckets before it. */
function buildBuckets(range, now = Date.now()) {
  const { buckets, unit } = RANGES[range];
  const starts = [];
  if (unit === "day") {
    const today = startOfDay(now);
    for (let i = buckets * 2 - 1; i >= 0; i--) starts.push(today - i * DAY);
    starts.push(today + DAY);
  } else {
    const thisMonth = startOfMonth(now);
    for (let i = buckets * 2 - 1; i >= 0; i--) starts.push(addMonths(thisMonth, -i));
    starts.push(addMonths(thisMonth, 1));
  }
  return starts; // length = buckets*2 + 1 (last is the exclusive end)
}

const labelFor = (ms, unit) =>
  unit === "day"
    ? new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : new Date(ms).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

const Delta = ({ now, before, neutral }) => {
  if (!before && !now) return <span className="text-xs text-muted-foreground">No change</span>;
  if (!before) return <span className="text-xs font-medium text-muted-foreground">New</span>;
  const pct = ((now - before) / before) * 100;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold"
      style={{
        background: neutral ? "hsl(var(--tint-amber))" : up ? "hsl(var(--tint-green))" : "hsl(var(--tint-red))",
        color: neutral ? "hsl(var(--on-amber))" : up ? "hsl(var(--on-green))" : "hsl(var(--on-red))",
      }}
    >
      <Icon size={12} />
      {Math.abs(pct).toFixed(pct > 99 ? 0 : 1)}%
    </span>
  );
};

const ChartTip = ({ active, payload, metric }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const fmt = (v) => (METRICS[metric].money ? inr(v) : `${v}`);
  return (
    <div className="rounded-xl border bg-popover/95 px-3 py-2.5 text-xs shadow-xl backdrop-blur">
      <p className="mb-1.5 font-semibold text-foreground">{row.name}</p>
      <div className="flex items-center justify-between gap-6">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <i className="h-2 w-2 rounded-full bg-primary" /> This period
        </span>
        <span className="num font-semibold text-foreground">{fmt(row.current)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-6">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <i className="h-2 w-2 rounded-full bg-muted-foreground/60" /> {row.prevName}
        </span>
        <span className="num font-medium text-muted-foreground">{fmt(row.previous)}</span>
      </div>
    </div>
  );
};

const RevenueChart = ({ range = "6months" }) => {
  const [credits, setCredits] = useState(null);
  const [error, setError] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [metric, setMetric] = useState("revenue");

  useEffect(() => {
    let alive = true;
    loadLedger(1000)
      .then((data) => {
        if (!alive) return;
        setCredits(data.entries.filter((e) => e.type === "credit" && e.status !== "cancelled" && e.date));
        setTruncated(!!data.truncated);
      })
      .catch((e) => alive && setError(e?.message || "Couldn't load revenue"));
    return () => {
      alive = false;
    };
  }, []);

  const model = useMemo(() => {
    if (!credits) return null;
    const cfg = RANGES[range] || RANGES["6months"];
    const starts = buildBuckets(range);
    const n = cfg.buckets;
    const totals = Object.fromEntries(
      Object.keys(METRICS).map((k) => [k, Array.from({ length: n * 2 }, () => 0)])
    );
    credits.forEach((c) => {
      if (c.date < starts[0] || c.date >= starts[starts.length - 1]) return;
      let i = starts.findIndex((s, idx) => idx < starts.length - 1 && c.date >= s && c.date < starts[idx + 1]);
      if (i < 0) return;
      totals.revenue[i] += c.amountPaid;
      totals.bookings[i] += 1;
      totals.earnings[i] += Math.max(c.amountPaid - c.amount, 0);
      totals.discounts[i] += c.couponDiscount || 0;
    });
    const sum = (arr) => arr.reduce((a, b) => a + b, 0);
    const kpis = Object.fromEntries(
      Object.keys(METRICS).map((k) => [
        k,
        { now: sum(totals[k].slice(n)), before: sum(totals[k].slice(0, n)) },
      ])
    );
    const series = Object.fromEntries(
      Object.keys(METRICS).map((k) => [
        k,
        Array.from({ length: n }, (_, i) => ({
          name: labelFor(starts[n + i], cfg.unit),
          prevName: labelFor(starts[i], cfg.unit),
          current: Math.round(totals[k][n + i] * 100) / 100,
          previous: Math.round(totals[k][i] * 100) / 100,
        })),
      ])
    );
    return { kpis, series, cfg };
  }, [credits, range]);

  const m = METRICS[metric];
  const empty = model && model.series[metric].every((p) => p.current === 0 && p.previous === 0);

  return (
    <div className="admin-card" style={{ padding: "22px 24px" }}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-primary" />
            <h2 className="text-[15px] font-semibold text-foreground">Revenue &amp; Growth</h2>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {model ? model.cfg.label : ""} compared with the period before it
          </p>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><i className="h-0.5 w-4 rounded bg-primary" /> This period</span>
          <span className="flex items-center gap-1.5"><i className="h-0 w-4 border-t-2 border-dashed border-muted-foreground/60" /> Previous</span>
        </div>
      </div>

      {/* KPI tiles double as the metric switch */}
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Object.entries(METRICS).map(([key, def]) => {
          const Icon = def.icon;
          const k = model?.kpis[key];
          const active = metric === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setMetric(key)}
              className={`rounded-xl border p-3.5 text-left transition-all ${
                active
                  ? "border-primary/40 bg-accent/60 ring-1 ring-primary/20"
                  : "bg-card hover:bg-muted/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    <Icon size={14} />
                  </span>
                  {def.label}
                </span>
                {k && <Delta now={k.now} before={k.before} neutral={def.lowerIsBetter} />}
              </div>
              <p className="num mt-2.5 text-2xl font-semibold text-foreground">
                {k ? (def.money ? inr(k.now) : k.now.toLocaleString("en-IN")) : "—"}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {def.hint}{k ? ` · was ${def.money ? inr(k.before) : k.before}` : ""}
              </p>
            </button>
          );
        })}
      </div>

      {model && (
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border bg-muted/40 px-3.5 py-2.5 text-[12.5px] text-muted-foreground">
          <span>Booking value</span>
          <b className="num text-foreground">{inr(model.kpis.revenue.now + model.kpis.discounts.now)}</b>
          <span>−</span>
          <span>discounts</span>
          <b className="num text-foreground">{inr(model.kpis.discounts.now)}</b>
          <span>=</span>
          <span>paid by patients</span>
          <b className="num text-foreground">{inr(model.kpis.revenue.now)}</b>
          <span className="mx-1 hidden sm:inline">·</span>
          <span>platform keeps</span>
          <b className="num text-foreground">{inr(model.kpis.earnings.now)}</b>
        </div>
      )}

      <div style={{ height: 300 }}>
        {error ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{error}</div>
        ) : !model ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading revenue…</div>
        ) : empty ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <p className="text-sm font-semibold text-foreground">No paid bookings in this period</p>
            <p className="text-xs text-muted-foreground">Try a longer range from the dropdown above.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={model.series[metric]} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 4" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                interval={model.series[metric].length > 12 ? 3 : 0}
                minTickGap={12}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={52}
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }}
                tickFormatter={(v) => (m.money ? inrShort(v) : v)}
              />
              <Tooltip content={<ChartTip metric={metric} />} cursor={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.25, strokeWidth: 1 }} />
              <Line
                type="monotone"
                dataKey="previous"
                stroke="hsl(var(--muted-foreground))"
                strokeOpacity={0.6}
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
                activeDot={false}
                isAnimationActive
              />
              <Area
                type="monotone"
                dataKey="current"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                fill="url(#gRev)"
                dot={{ r: 3, strokeWidth: 2, fill: "hsl(var(--card))", stroke: "hsl(var(--primary))" }}
                activeDot={{ r: 5, strokeWidth: 2, fill: "hsl(var(--card))", stroke: "hsl(var(--primary))" }}
                isAnimationActive
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      {truncated && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Based on the latest 1,000 bookings; older ones aren&apos;t counted.
        </p>
      )}
    </div>
  );
};

export default RevenueChart;
